import { useVirtualizer } from '@tanstack/react-virtual';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useDebounceCallback, useOnClickOutside } from 'usehooks-ts';

import { InputAutoCompleteMultipleI } from './input-autocomplete-multiple.interface';
import Chip from '../chip/chip';
import Icon from '../icon/icon';
import {
    ACCORDION_PADDING,
    MIN_OPTION_HEIGHT,
    MIN_OPTION_VISIBLE,
} from '../input-autocomplete/input-autocomplete.constants';
import {
    filterQuerySimple,
    handleAutocompleteRemoteQuery,
    renderOptionSimple,
} from '../input-autocomplete/input-autocomplete.utils';
import { getInputContainerClasses } from '../input/input';

export const InputAutoCompleteMultiple = ({
    name,
    label,
    testId,
    value,
    options,
    isLoading = false,
    minVisible = MIN_OPTION_VISIBLE,
    optionDropdownClasses,
    optionClasses,
    containerClasses,
    labelClasses,
    debounceTime = 3000,
    filterRemote,
    wrapPills = false,
    pillCount = 3,
    onChange,
    onInputChange,
    onScrollToEnd,
    onAllSelected,
    callback,
    selectAllOption = false,
    required,
    creatable = false,
    onValuesChange,

    ...props
}: InputAutoCompleteMultipleI) => {
    const refMain = useRef<HTMLDivElement | null>(null);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const calledQuery = useRef<string>('');

    // options change control
    const optionLengthControl = useRef<{ continue: boolean; length: number }>({
        continue: true,
        length: options.length,
    });

    const [show, setShow] = useState(false);
    const [query, setQuery] = useState<string>('');
    const [valueSelected, setValueSelected] = useState<string[]>(value ?? []);

    // Handle click outside
    useOnClickOutside(refMain, () => setShow(false));

    // Debounce query
    const debounced = useDebounceCallback(
        (value) => {
            if (value.length && options.length) {
                callback && callback(value);
            }
        },
        debounceTime,
        { leading: true },
    );

    // Options handling empty option
    const filteredOptions = useMemo(() => {
        return valueSelected.map((i) => i.toLocaleLowerCase()).includes(query.toLocaleLowerCase())
            ? options
            : filterQuerySimple(options, query);
    }, [options, query, valueSelected]);

    const dynamicMinOptions = useMemo(() => {
        return minVisible < filteredOptions.length ? minVisible : filteredOptions.length;
    }, [minVisible, filteredOptions]);

    const dynamicHeight = useMemo(() => {
        return show ? (filteredOptions.length ? MIN_OPTION_HEIGHT * dynamicMinOptions + ACCORDION_PADDING * 2 : 0) : 0;
    }, [filteredOptions, dynamicMinOptions, show]);

    const rowVirtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => modalRef.current,
        estimateSize: () => MIN_OPTION_HEIGHT,
    });

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setQuery(newText);
        onInputChange && onInputChange(newText);
    };

    const handleSelect = (value: string) => {
        const isSelected = valueSelected.includes(value);
        const newValues = isSelected ? valueSelected.filter((item) => item !== value) : [...valueSelected, value];

        setValueSelected(newValues);
        setQuery('');
        onChange && onChange(value);
        onValuesChange && onValuesChange(newValues);
    };

    const multipleDeleteSelected = (valueToDelete: string) => {
        const newValues = valueSelected.filter((value) => value !== valueToDelete);
        setValueSelected(newValues);
        onChange && onChange(valueToDelete);
        onValuesChange && onValuesChange(newValues);
    };

    const handleMultipleDeleteSelected = (evt?: React.MouseEvent) => {
        const chipValue = evt?.currentTarget?.parentElement?.querySelector('div:first-of-type')?.textContent;
        if (chipValue) multipleDeleteSelected(chipValue);
    };

    const handleMultipleDeleteAllSelected = (_evt?: React.MouseEvent) => {
        setValueSelected([]);
        const returnValues = options
            .filter((item) => {
                return valueSelected.includes(item as unknown as string);
            })
            .map((value) => {
                return value as unknown as string;
            });

        onAllSelected && onAllSelected(returnValues);
        onValuesChange && onValuesChange([]);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (creatable && event.key === 'Enter' && query.trim()) {
            event.preventDefault();
            const newValue = query.trim();
            if (!valueSelected.includes(newValue)) {
                const newValues = [...valueSelected, newValue];
                setValueSelected(newValues);
                onValuesChange && onValuesChange(newValues);
                setQuery('');
            }
        }
    };

    // handle scroll end
    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem) {
            return;
        }

        const isAtEndOfList = lastItem.index === filteredOptions.length - 1;
        const optionLengthHasChanged = optionLengthControl.current.continue;
        const filteredGotQuery = query
            ? filteredOptions.some((item) => item.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
            : false;

        if (isAtEndOfList && !isLoading && optionLengthHasChanged && !filteredGotQuery) {
            onScrollToEnd && onScrollToEnd();
            optionLengthControl.current = {
                ...optionLengthControl.current,
                continue: false,
            };
        } else if (!optionLengthControl.current.continue && optionLengthControl.current.length < options.length) {
            optionLengthControl.current = {
                continue: true,
                length: options.length,
            };
        }

        // Do NOT delete eslint bypass, change rowVirtualizer.getVirtualItems() to rowVirtualizer
        // it makes useeffect doesnt work properly
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        rowVirtualizer.getVirtualItems(),
        filteredOptions,
        options,
        onScrollToEnd,
        isLoading,
    ]);

    // handle trigger callback
    useEffect(() => {
        handleAutocompleteRemoteQuery({
            query,
            filteredOptionsLength: filteredOptions.length,
            isLoading,
            filterRemote,
            calledQueryRef: calledQuery,
            shouldOpenDropdown:
                filteredOptions.length > 0 &&
                !show &&
                query.length > 0 &&
                !valueSelected.map((i) => i.toLocaleLowerCase()).includes(query.toLocaleLowerCase()),
            openDropdown: () => setShow(true),
            debounced,
        });
    }, [debounced, filterRemote, filteredOptions, isLoading, query, show, valueSelected]);

    const optionsContainerClasses = twMerge(
        'overflow-auto w-full shadow-lg border border-gray-50 rounded-b-lg bg-white relative z-50 ',
        show && filteredOptions.length > 0 && 'py-2',
        optionDropdownClasses,
    );
    const optionItemClasses = twMerge(
        'flex justify-start items-center pl-2 font-light hover:bg-gray-50',
        optionClasses,
    );

    const handleSelectAll = () => {
        const newValues = options.map((value) => value);
        if (newValues.every((item) => valueSelected.includes(item))) {
            setValueSelected([]);
        } else {
            setValueSelected(newValues);
        }

        const returnValues = options.map((value) => value as unknown as string);

        onAllSelected && onAllSelected(returnValues);
    };

    //Reset value selected on value change
    useEffect(() => {
        if (value && JSON.stringify(value) !== JSON.stringify(valueSelected)) {
            setValueSelected(value);
        }
    }, [value]);

    return (
        <div
            className={containerClasses}
            data-testid={testId || `${name}-autocomplete`}
            data-test-id={testId || `${name}-autocomplete`}
            ref={refMain}
        >
            <div className="flex flex-col w-full">
                <label className={twMerge('mb-1 text-sm font-medium text-black', labelClasses)} htmlFor={name}>
                    {label}
                    {required && <span className="text-red-600"> * </span>}
                </label>
                <div
                    className={twMerge(
                        getInputContainerClasses({ ...props }),
                        'flex gap-1 justify-between',
                        wrapPills && 'flex-wrap',
                        props.errorMessage && 'border-red-600',
                    )}
                    onClick={(evt) => {
                        evt.currentTarget.querySelector('input')?.focus();
                        setShow((prevState) => !prevState);
                    }}
                >
                    {(valueSelected as string[]).length <= pillCount ? (
                        (valueSelected as string[]).map((value: string) => (
                            <Chip
                                className="m-0 shrink-0"
                                text={value}
                                size="xxs"
                                onClose={handleMultipleDeleteSelected}
                                disabled={props.disabled ?? false}
                            />
                        ))
                    ) : (
                        <Chip
                            className="m-0 shrink-0 grow-0"
                            text={`${(valueSelected as string[]).length} seleccionados`}
                            size="xxs"
                            onClose={handleMultipleDeleteAllSelected}
                            disabled={props.disabled ?? false}
                        />
                    )}
                    <input
                        className="outline-none shrink w-full"
                        name={name}
                        value={query}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={props.placeholder}
                        data-test-id={`${name}-combo-input`}
                        disabled={props.disabled ?? false}
                    />
                    {isLoading ? (
                        <div className={twMerge(' transition-all', isLoading && 'text-primary animate-spin')}>
                            <Icon name="ri-loader-4-line" className="text-gray-100" size={20} />
                        </div>
                    ) : (
                        <div
                            className={twMerge('transition shrink-0 grow-0 h-5', show && 'rotate-180')}
                            data-test-id="arrow-down"
                        >
                            <Icon name="ri-arrow-down-s-line" className="text-gray-100" size={20} />
                        </div>
                    )}
                </div>
            </div>
            <div
                ref={modalRef}
                className={optionsContainerClasses}
                style={
                    dynamicHeight === 0
                        ? { height: 0, padding: 0, border: 'none' }
                        : { height: dynamicHeight, display: 'block' }
                }
                data-testid={`${name}-autocomplete-container`}
            >
                <div
                    className="leading-none"
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {selectAllOption && (
                        <button
                            onClick={handleSelectAll}
                            className="flex w-full justify-start items-center pl-8 hover:bg-gray-50 h-8"
                        >
                            Seleccionar todo
                        </button>
                    )}
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        return renderOptionSimple({
                            name,
                            item: filteredOptions[virtualRow.index] as string,
                            isSelected: valueSelected
                                .map((i) => i.toLocaleLowerCase())
                                .includes(filteredOptions[virtualRow.index].toLocaleLowerCase()),
                            style: {
                                position: 'absolute',
                                top: selectAllOption ? virtualRow.size : 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualRow.size}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            },
                            classes: optionItemClasses,
                            action: handleSelect,
                            disabled: false,
                            multiple: true,
                        });
                    })}
                </div>
            </div>
        </div>
    );
};

export default InputAutoCompleteMultiple;
